import { PopupCampaignPlatform } from "../api/generated/models/PopupCampaignPlatform";
import { PopupCampaignTargeting } from "../api/generated/models/PopupCampaignTargeting";
import { PopupCampaignPostalCodeViewModel } from "../api/generated/models/PopupCampaignPostalCodeViewModel";

/**
 * Flat form state for the Audience card. Numbers are kept as strings because they come from
 * text inputs and an empty string has to stay distinguishable from zero — "no maximum balance"
 * and "a maximum balance of 0 kr" are different audiences.
 *
 * Mirrors the shape `segmentedPushOptions.ts` uses for the segmented-push audience form.
 */
export interface TargetingFormState {
  /** "" means any gender. The backend rejects NotSelected, so "any" must omit the field. */
  gender: "" | "Male" | "Female";
  ageMin: string;
  ageMax: string;
  radiusLat: number | null;
  radiusLon: number | null;
  radiusKm: string;
  locationPostalCodes: string[];
  locationCities: string[];
  locationRegions: string[];
  platforms: PopupCampaignPlatform[];
  oisterTenureMin: string;
  oisterTenureMax: string;
  appTenureMin: string;
  appTenureMax: string;
  balanceMin: string;
  balanceMax: string;
  balanceHasEverEarned: boolean;
}

export const EMPTY_TARGETING_FORM: TargetingFormState = {
  gender: "",
  ageMin: "",
  ageMax: "",
  radiusLat: null,
  radiusLon: null,
  radiusKm: "",
  locationPostalCodes: [],
  locationCities: [],
  locationRegions: [],
  platforms: [],
  oisterTenureMin: "",
  oisterTenureMax: "",
  appTenureMin: "",
  appTenureMax: "",
  balanceMin: "",
  balanceMax: "",
  balanceHasEverEarned: false,
};

const sameArray = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * Flat comparison of two targeting form states. Array fields are compared element-wise (order
 * matters — a reordered selection counts as a change) so two array instances holding the same
 * values in the same order are equal; scalar fields compare with `===`.
 */
export const targetingEquals = (a: TargetingFormState, b: TargetingFormState): boolean =>
  a.gender === b.gender &&
  a.ageMin === b.ageMin &&
  a.ageMax === b.ageMax &&
  a.radiusLat === b.radiusLat &&
  a.radiusLon === b.radiusLon &&
  a.radiusKm === b.radiusKm &&
  sameArray(a.locationPostalCodes, b.locationPostalCodes) &&
  sameArray(a.locationCities, b.locationCities) &&
  sameArray(a.locationRegions, b.locationRegions) &&
  sameArray(a.platforms, b.platforms) &&
  a.oisterTenureMin === b.oisterTenureMin &&
  a.oisterTenureMax === b.oisterTenureMax &&
  a.appTenureMin === b.appTenureMin &&
  a.appTenureMax === b.appTenureMax &&
  a.balanceMin === b.balanceMin &&
  a.balanceMax === b.balanceMax &&
  a.balanceHasEverEarned === b.balanceHasEverEarned;

export const TARGETING_AGE_MIN = 0;
export const TARGETING_AGE_MAX = 120;
export const TARGETING_BALANCE_MAX_KR = 1_000_000;
export const TARGETING_RADIUS_MIN_KM = 1;
export const TARGETING_RADIUS_MAX_KM = 500;
/** Denmark's bounding box with a margin, mirroring the backend's coordinate validation. */
export const TARGETING_LAT_MIN = 54.4;
export const TARGETING_LAT_MAX = 57.9;
export const TARGETING_LON_MIN = 7.9;
export const TARGETING_LON_MAX = 15.3;

const num = (value: string): number | undefined => {
  if (value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const str = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : String(value);

/** A radius is only meaningful once the admin has actually placed a point on the map. */
const hasRadius = (form: TargetingFormState): boolean =>
  form.radiusLat !== null && form.radiusLon !== null && num(form.radiusKm) !== undefined;

export const hasLocationInput = (form: TargetingFormState): boolean =>
  hasRadius(form) ||
  form.locationPostalCodes.length > 0 ||
  form.locationCities.length > 0 ||
  form.locationRegions.length > 0;

/**
 * Form -> API document. Returns undefined when no dimension is constrained, because an empty
 * document and no targeting are the same campaign: one that reaches everyone.
 * `resolved` is never sent — the server computes it and overwrites anything we send.
 */
export const toTargetingDto = (form: TargetingFormState): PopupCampaignTargeting | undefined => {
  const age = num(form.ageMin) !== undefined || num(form.ageMax) !== undefined
    ? { min: num(form.ageMin), max: num(form.ageMax) }
    : undefined;

  const oisterTenure = num(form.oisterTenureMin) !== undefined || num(form.oisterTenureMax) !== undefined
    ? { minMonths: num(form.oisterTenureMin), maxMonths: num(form.oisterTenureMax) }
    : undefined;

  const appTenure = num(form.appTenureMin) !== undefined || num(form.appTenureMax) !== undefined
    ? { minMonths: num(form.appTenureMin), maxMonths: num(form.appTenureMax) }
    : undefined;

  const balance = num(form.balanceMin) !== undefined || num(form.balanceMax) !== undefined || form.balanceHasEverEarned
    ? { minKr: num(form.balanceMin), maxKr: num(form.balanceMax), hasEverEarned: form.balanceHasEverEarned }
    : undefined;

  const location = hasLocationInput(form)
    ? {
        radius: hasRadius(form)
          ? { lat: form.radiusLat!, lon: form.radiusLon!, km: num(form.radiusKm)! }
          : undefined,
        postalCodes: form.locationPostalCodes.length > 0 ? form.locationPostalCodes : undefined,
        cities: form.locationCities.length > 0 ? form.locationCities : undefined,
        regions: form.locationRegions.length > 0 ? form.locationRegions : undefined,
      }
    : undefined;

  const targeting: PopupCampaignTargeting = {
    gender: form.gender === "" ? undefined : (form.gender as PopupCampaignTargeting["gender"]),
    age,
    location,
    platforms: form.platforms.length > 0 ? form.platforms : undefined,
    oisterTenure,
    appTenure,
    balance,
  };

  const constrained = Object.values(targeting).some((value) => value !== undefined);
  return constrained ? targeting : undefined;
};

/** API document -> form. Null targeting and an empty document both give the empty form. */
export const fromTargetingViewModel = (
  targeting: PopupCampaignTargeting | null | undefined
): TargetingFormState => {
  if (!targeting) return { ...EMPTY_TARGETING_FORM };

  const location = targeting.location;

  // The generated Gender enum also carries NotSelected and Other, which this form cannot
  // represent (the backend rejects both as targeting values). Anything but Male/Female
  // degrades to "any gender" rather than surviving into a value toTargetingDto would resubmit.
  const gender: TargetingFormState["gender"] =
    targeting.gender === "Male" || targeting.gender === "Female" ? targeting.gender : "";

  return {
    gender,
    ageMin: str(targeting.age?.min),
    ageMax: str(targeting.age?.max),
    radiusLat: location?.radius?.lat ?? null,
    radiusLon: location?.radius?.lon ?? null,
    radiusKm: str(location?.radius?.km),
    locationPostalCodes: [...(location?.postalCodes ?? [])],
    locationCities: [...(location?.cities ?? [])],
    locationRegions: [...(location?.regions ?? [])],
    platforms: [...(targeting.platforms ?? [])],
    oisterTenureMin: str(targeting.oisterTenure?.minMonths),
    oisterTenureMax: str(targeting.oisterTenure?.maxMonths),
    appTenureMin: str(targeting.appTenure?.minMonths),
    appTenureMax: str(targeting.appTenure?.maxMonths),
    balanceMin: str(targeting.balance?.minKr),
    balanceMax: str(targeting.balance?.maxKr),
    balanceHasEverEarned: targeting.balance?.hasEverEarned ?? false,
  };
};

export type TargetingErrors = Partial<Record<string, string>>;

/** age and both tenures are `int?` on the backend; an overflow would 400 just as opaquely. */
const INT32_MAX = 2_147_483_647;

/**
 * Mirrors the backend's PopupCampaignTargetingValidator so the admin gets an inline message
 * instead of a 400. Keep the bounds in step with it.
 */
export const validateTargeting = (form: TargetingFormState): TargetingErrors => {
  const errors: TargetingErrors = {};

  const range = (
    minValue: string,
    maxValue: string,
    minKey: string,
    maxKey: string,
    lower: number,
    upper: number | undefined,
    label: string,
    integerOnly = false
  ) => {
    const min = num(minValue);
    const max = num(maxValue);

    const checkBound = (value: number | undefined, key: string) => {
      if (value === undefined) return;
      if (integerOnly && !Number.isInteger(value)) {
        errors[key] = `${label} must be a whole number`;
        return;
      }
      // int? on the backend overflows silently past this range into the same opaque 400 a
      // fractional value would — catch it here even where there is no declared upper bound.
      if (integerOnly && (value > INT32_MAX || value < -INT32_MAX - 1)) {
        errors[key] = `${label} is too large`;
        return;
      }
      if (value < lower || (upper !== undefined && value > upper)) {
        errors[key] = upper !== undefined
          ? `${label} must be between ${lower} and ${upper}`
          : `${label} must be at least ${lower}`;
      }
    };

    checkBound(min, minKey);
    checkBound(max, maxKey);
    if (!errors[maxKey] && min !== undefined && max !== undefined && min > max) {
      errors[maxKey] = `Maximum must not be less than minimum`;
    }
  };

  range(form.ageMin, form.ageMax, "ageMin", "ageMax", TARGETING_AGE_MIN, TARGETING_AGE_MAX, "Age", true);
  range(form.balanceMin, form.balanceMax, "balanceMin", "balanceMax", 0, TARGETING_BALANCE_MAX_KR, "Balance");
  // The backend enforces only a >= 0 floor on tenure — no upper bound to mirror here, but tenure
  // is still `int?`, so an out-of-int32-range value still gets the overflow guard above.
  range(form.oisterTenureMin, form.oisterTenureMax, "oisterTenureMin", "oisterTenureMax", 0, undefined, "Tenure", true);
  range(form.appTenureMin, form.appTenureMax, "appTenureMin", "appTenureMax", 0, undefined, "Tenure", true);

  // Coordinates only matter once hasRadius is true — that's the same gate toTargetingDto uses
  // to decide whether to send location.radius at all. A pin placed with no radiusKm typed in
  // yet (and no other location input) submits no location whatsoever, so validating it here
  // would block a save over a coordinate that is never transmitted.
  if (hasRadius(form)) {
    const km = num(form.radiusKm)!;
    if (km < TARGETING_RADIUS_MIN_KM || km > TARGETING_RADIUS_MAX_KM) {
      errors.radiusKm = `Radius must be between ${TARGETING_RADIUS_MIN_KM} and ${TARGETING_RADIUS_MAX_KM} km`;
    }
    if (form.radiusLat! < TARGETING_LAT_MIN || form.radiusLat! > TARGETING_LAT_MAX) {
      errors.radiusLat = `Latitude must be between ${TARGETING_LAT_MIN} and ${TARGETING_LAT_MAX}`;
    }
    if (form.radiusLon! < TARGETING_LON_MIN || form.radiusLon! > TARGETING_LON_MAX) {
      errors.radiusLon = `Longitude must be between ${TARGETING_LON_MIN} and ${TARGETING_LON_MAX}`;
    }
  }

  return errors;
};

export interface LocationOptions {
  codes: string[];
  cities: string[];
  regions: string[];
}

/** Danish collation, so "København" and "Aarhus" sort the way an admin expects. */
const sortDanish = (values: Iterable<string>): string[] =>
  Array.from(values).sort((a, b) => a.localeCompare(b, "da"));

/** The three option lists the location multi-selects offer, derived from the reference table. */
export const deriveLocationOptions = (
  postalCodes: PopupCampaignPostalCodeViewModel[]
): LocationOptions => ({
  codes: sortDanish(new Set(postalCodes.map((p) => p.code).filter(Boolean) as string[])),
  cities: sortDanish(new Set(postalCodes.map((p) => p.city).filter(Boolean) as string[])),
  regions: sortDanish(new Set(postalCodes.flatMap((p) => p.regions ?? []))),
});

/**
 * Danish names are the search keys here, and NFD normalisation alone does not fold them: "å"
 * decomposes into a + combining ring, but "ø" is a stroke and "æ" a ligature, and neither
 * decomposes at all. Without an explicit map the 220 of 605 seeded city names containing æ/ø/å
 * are unreachable unless the admin produces the exact character.
 *
 * "aa" collapses to "a" after the fold so Aarhus / Århus / arhus converge on one key — DAWA
 * spells it "Aarhus C" while plenty of people still type "Århus". The leading NFC pass matters
 * because a decomposed "å" would otherwise slip past the replace.
 */
const foldDanish = (value: string): string =>
  value
    .normalize("NFC")
    .toLowerCase()
    .replace(/ø/g, "o")
    .replace(/æ/g, "ae")
    .replace(/å/g, "a")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/aa/g, "a");

/**
 * DAWA is the Danish address registry, so every name in the reference table is Danish while the
 * dashboard is in English. Each pair appends the English exonym to the key of any option whose
 * folded name contains the Danish term, so "Copenhagen" finds the seven "København …" districts.
 * Extending the option's key rather than rewriting the query keeps partial input working, so
 * "copen" matches too.
 */
const LOCATION_SEARCH_ALIASES: ReadonlyArray<readonly [danish: string, english: string]> = [
  ["kobenhavn", "copenhagen"],
  ["helsingor", "elsinore"],
  ["sjaelland", "zealand"],
  ["jylland", "jutland"],
];

const locationSearchKey = (option: string): string => {
  const folded = foldDanish(option);
  const aliases = LOCATION_SEARCH_ALIASES
    .filter(([danish]) => folded.includes(danish))
    .map(([, english]) => english);
  return aliases.length === 0 ? folded : `${folded} ${aliases.join(" ")}`;
};

/** Diacritic- and language-insensitive substring match. An empty query matches everything. */
export const matchesLocationQuery = (option: string, query: string): boolean => {
  const folded = foldDanish(query.trim());
  return folded === "" || locationSearchKey(option).includes(folded);
};

/**
 * Every option matching the query, in the order given and never truncated. The caller decides how
 * many to render; the full count is what tells the admin the list they can see is not the whole
 * list, and what "add all" acts on.
 */
export const filterLocationOptions = (options: string[], query: string): string[] =>
  options.filter((option) => matchesLocationQuery(option, query));

export interface EstimateDescription {
  headline: string;
  detail: string;
  calculating: boolean;
}

/**
 * Only what the tile needs. Deliberately not the generated view model: the generator types
 * NodaTime's Instant as Record<string, any>, so taking the view model directly would force a
 * cast on every caller. A generated PopupCampaignAudienceEstimateViewModel is assignable to this.
 */
export interface EstimateLike {
  eligibleUsers?: number | null;
  isPending?: boolean;
  calculatedAt?: unknown;
  /** Whether the server actually restricted the count to the targeted platforms. */
  narrowedByPlatform?: boolean;
}

/**
 * How the reach tile reads. `eligibleUsers` is null until the worker's first pass, which is a
 * different thing from an audience of zero — never render null as "0 users".
 *
 * Native platform targeting IS counted, by requiring a push token for the targeted platform. Web
 * targeting is not, because nothing stored says who might open the web app — so a selection that
 * includes a web platform returns the whole eligible audience. `narrowedByPlatform` comes from the
 * server, from the same rule that produced the number, and the tile must say when it is false and
 * platforms were nonetheless chosen: an unnarrowed count read as platform-specific is the failure
 * worth guarding against.
 */
export const describeEstimate = (
  estimate: EstimateLike | undefined | null,
  selectedPlatforms: readonly unknown[] = []
): EstimateDescription => {
  const users = estimate?.eligibleUsers;
  const pending = estimate?.isPending ?? true;
  const known = users !== null && users !== undefined;

  // calculatedAt is an ISO string at runtime but Record<string, any> to the type system.
  const stamp = typeof estimate?.calculatedAt === "string" ? new Date(estimate.calculatedAt) : null;
  const when = stamp && !Number.isNaN(stamp.getTime())
    ? stamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  if (!known) {
    return { headline: "—", detail: "Calculating — this usually takes under a minute.", calculating: true };
  }

  const countedAt = when ? ` Counted at ${when}.` : "";
  const platformNote = selectedPlatforms.length === 0
    ? ""
    : estimate?.narrowedByPlatform
      ? " Limited to your selected platforms."
      : " Platform targeting is NOT applied: web reach cannot be estimated, so this covers every eligible user.";

  return {
    headline: users!.toLocaleString("en-US"),
    detail: pending
      ? "Recalculating — showing the previous number."
      : `Users not yet reached.${platformNote}${countedAt}`,
    calculating: pending,
  };
};
